import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FiChecklist2ReportWidgetBaseDirective } from '../../../shared/fi-checklist2-report-widget-base.directive';
import { cloneDeep } from '@fi-shared/utils';
import {
    IChecklist2ReportRecentActivity,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Component({
    selector: 'fi-recent-activity-widget',
    templateUrl: './recent-activity-widget.component.html',
    styleUrls: ['./recent-activity-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-recent-activity-widget' },
    standalone: false,
})
export class FiChecklist2ReportRecentActivityWidget extends FiChecklist2ReportWidgetBaseDirective implements OnInit {
    @Output() onCloseDialog = new EventEmitter<void>();

    protected filteredRecentActivity$ = new BehaviorSubject<IChecklist2ReportRecentActivity[]>([]);

    protected get loading$(): Observable<boolean> {
        return this.fiChecklist2ReportDataService.getLoading$(this.reportParameter.Id);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this.resetFilter();
        this.filteredRecentActivity$.next(this.recentActivity);
    }

    protected setServices(): void {
        super.setServices();
    }

    protected isSavedFiltersMenuShown(): boolean {
        return !!this.reportParameter.Settings.Filters?.length;
    }

    protected getAppliedFilterSettingCounter(): number {
        return Object.values(this.currentFilter)
            .filter(Array.isArray)
            .reduce((counter, arr) => counter + arr.length, 0);
    }

    protected selectSavedFilter(newFilter: IChecklistV2ReportParameterFilter): void {
        this.currentFilter = cloneDeep(newFilter);
        this.reportParameter.Settings.CurrentFilterGuid = newFilter.Guid;
        this.fiChecklist2ReportDataService.setWidgetFilter(this.reportParameter);
        this.updateFilteredRecentActivity();
    }

    private updateFilteredRecentActivity() {
        this.fiChecklist2ReportRecentActivityService
            .getChecklistRecentActivity(
                this.currentFilter,
                this.fiChecklist2ReportDataService.getRecentHistoryDateRange(),
                this.reportData.auditMode,
            )
            .then((result: IChecklist2ReportRecentActivity[]) => {
                this.filteredRecentActivity$.next(result);
            });
    }

    protected isResetButtonVisible(): boolean {
        return !!this.reportParameter.Settings.Filters?.find((filter: IChecklistV2ReportParameterFilter) => {
            return filter.Guid === this.currentFilter.Guid;
        });
    }

    protected resetFilter(): void {
        this.currentFilter = cloneDeep(this.getNewFilterInstance(undefined));
        this.reportParameter.Settings.CurrentFilterGuid = this.currentFilter.Guid;
        this.cdr.markForCheck();
        this.fiChecklist2ReportDataService.setWidgetFilter(this.reportParameter);
        this.updateFilteredRecentActivity();
    }

    protected openRecentActivityDialog(): void {
        this.fiChecklist2ReportHelperService
            .showRecentActivityDialog({
                reportParameter: this.reportParameter,
                reportData: this.reportData,
                currentFilter: this.currentFilter,
            })
            .afterClosed((result) => {
                this.reportParameter = cloneDeep(result.value.reportParameter);

                if (this.fiChecklist2ReportHelperService.isFilterEmpty(result.value.currentFilter)) {
                    this.resetFilter();
                    return;
                }

                this.currentFilter = cloneDeep(result.value.currentFilter);

                this.updateFilteredRecentActivity();
                this.cdr.markForCheck();
            });
    }

    protected getCurrentFilterName(): string {
        if (!!this.currentFilter.Name?.length) {
            return this.currentFilter.Name;
        }

        return this.appTextService.get('common.saved_filters');
    }

    public closeDialog(): void {
        this.onCloseDialog.emit();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
